import React, { useCallback, useEffect, useRef, useState } from "react";

import useExamOptions from "../hooks/useExamOptions";
import { getExamLessons, getExamModules, getExamStructure } from "../services/examsApi";
import {
  createExam,
  getCinetPayPaymentStatus,
  getExamStatus,
  initializeCinetPayPayment,
  payExamWithWallet,
  previewExam,
} from "../services/generationApi";
import {
  CINETPAY_ACTIVE_STATES,
  CINETPAY_TERMINAL_STATES,
  claimCinetPayInitialization,
  clearCinetPayAttempt,
  normalizeCinetPayStatus,
  persistCinetPayAttempt,
  readCinetPayAttempt,
  releaseCinetPayInitialization,
  restoredCinetPayState,
} from "../services/cinetPayAttempt";
import { openCinetPayExternally } from "../services/externalCheckout";
import ExamGeneralOptions from "./ExamGeneralOptions";
import ExamGenerationResult from "./ExamGenerationResult";
import ExamPreview from "./ExamPreview";
import ExerciseBrowser from "./ExerciseBrowser";
import { isTwoSituationExercise } from "./examStructure";
import ModuleLessonSelector from "./ModuleLessonSelector";
import ExamFlowDialog from "./ExamFlowDialog";
import { correctionAvailability, describeGenerationError, nativeExamFlowEnabled } from "./nativeExamFlow";

const initialDraft = {
  establishmentId: "",
  classe: "",
  matiere: "",
  session: "",
  academicYear: "",
  duration: "",
  coefficient: "",
  title: "",
  examiner: "",
  period: "",
  examDate: "",
  headerStyle: 0,
  tier: "",
  moduleIds: [],
  lessonIds: [],
};

const getLabel = (items, value) => items.find((item) => (item.code ?? item.id) === value)?.label
  || items.find((item) => String(item.id) === String(value))?.nom
  || "—";

const isExerciseCompatible = (exercise, draft) => {
  if (draft.matiere && exercise.matiere !== draft.matiere) return false;
  if (draft.classe && !exercise.classes?.some((item) => item.code === draft.classe)) return false;
  if (draft.session && exercise.session !== draft.session) return false;
  if (draft.moduleIds.length && !exercise.modules?.some((item) => draft.moduleIds.includes(item.id))) return false;
  if (draft.lessonIds.length && !exercise.lessons?.some((item) => draft.lessonIds.includes(item.id))) return false;
  return true;
};

const createIdempotencyKey = () => window.crypto?.randomUUID?.()
  || `ql-${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;

export { openCinetPayExternally } from "../services/externalCheckout";

const generationErrorMessage = (error) => {
  if (!error.response) return "Le résultat de la génération n’a pas pu être confirmé.";
  const detail = typeof error.response.data?.detail === "string" ? error.response.data.detail : null;
  if (detail) return detail;
  const messages = {
    400: "Les informations de l’épreuve sont invalides.",
    403: "Ce compte n’est pas autorisé à générer une épreuve.",
    409: "Cette tentative de génération ne peut pas être relancée dans son état actuel.",
    500: "La génération des documents a échoué.",
    503: "Le service de génération est temporairement indisponible.",
  };
  return messages[error.response.status] || "Impossible de générer l’épreuve.";
};

export default function ExamGenerator({ wallet, onWalletUpdate, onWalletRefresh, onWalletPayment, onBack, onManageEstablishments, initialExamId = null }) {
  const enhancedNativeFlow = nativeExamFlowEnabled;
  const { options, establishments, loading, error, reload } = useExamOptions();
  const [draft, setDraft] = useState(initialDraft);
  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [modules, setModules] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [modulesLoading, setModulesLoading] = useState(false);
  const [lessonsLoading, setLessonsLoading] = useState(false);
  const [modulesError, setModulesError] = useState(null);
  const [lessonsError, setLessonsError] = useState(null);
  const [selectedExercises, setSelectedExercises] = useState([]);
  const [examPositions, setExamPositions] = useState([]);
  const [selectionNotice, setSelectionNotice] = useState("");
  const [generationState, setGenerationState] = useState({ status: "idle", error: null, retryAvailable: false, result: null, refreshing: false, refreshError: null });
  const [paymentState, setPaymentState] = useState({ status: "idle", error: null });
  const [cinetPayState, setCinetPayState] = useState({ status: "idle", error: null, checking: false, paymentUrl: null });
  const [resumeState, setResumeState] = useState({ open: false, examId: "", loading: false, error: null });
  const [previewState, setPreviewState] = useState({ loading: false, error: null, url: null, payload: null });
  const [flowDialog, setFlowDialog] = useState(null);
  const [generationFailure, setGenerationFailure] = useState(null);
  const defaultsApplied = useRef(false);
  const generationLock = useRef(false);
  const generationAttempt = useRef(null);
  const paymentLock = useRef(false);
  const cinetPayLock = useRef(false);
  const initialResumeStarted = useRef(false);
  const previewUrl = useRef(null);
  const previewLock = useRef(false);

  useEffect(() => () => { if (previewUrl.current) URL.revokeObjectURL(previewUrl.current); }, []);

  const focusField = (fieldId) => {
    window.setTimeout(() => {
      const field = document.getElementById(fieldId);
      field?.scrollIntoView?.({ behavior: "smooth", block: "center" });
      field?.focus?.({ preventScroll: true });
    }, 0);
  };

  const closeDialogAndFocus = (fieldId) => {
    setFlowDialog(null);
    if (fieldId) focusField(fieldId);
  };

  const invalidatePreview = (message = "Les paramètres ont changé. Actualisez la prévisualisation avant de confirmer.") => {
    if (!enhancedNativeFlow || !previewState.payload) return;
    if (previewUrl.current) URL.revokeObjectURL(previewUrl.current);
    previewUrl.current = null;
    setPreviewState({ loading: false, error: message, url: null, payload: null });
    setGenerationFailure(null);
  };

  useEffect(() => {
    if (!initialExamId || initialResumeStarted.current) return;
    initialResumeStarted.current = true;
    setResumeState({ open: false, examId: String(initialExamId), loading: true, error: null });
    getExamStatus(initialExamId).then((result) => {
      setGenerationState({ status: "success", error: null, retryAvailable: false, result, refreshing: false, refreshError: null });
      setPaymentState({ status: result.paid ? "success" : "idle", error: null });
      setResumeState({ open: false, examId: "", loading: false, error: null });
      window.scrollTo({ top: 0, behavior: "smooth" });
    }).catch((requestError) => {
      const status = requestError.response?.status;
      const messages = {
        403: "Vous n’êtes pas autorisé à consulter cette épreuve.",
        404: "Cette épreuve est introuvable.",
      };
      setResumeState({ open: true, examId: String(initialExamId), loading: false, error: messages[status] || "Impossible de charger cette épreuve." });
    });
  }, [initialExamId]);

  useEffect(() => {
    if (!options || defaultsApplied.current) return;
    defaultsApplied.current = true;
    setDraft((current) => ({
      ...current,
      academicYear: options.defaults?.annee_academique || "",
      duration: options.defaults?.duree || "",
    }));
  }, [options]);

  const loadModules = useCallback(async () => {
    if (!draft.matiere || !draft.classe) return;
    setModulesLoading(true);
    setModulesError(null);
    try {
      setModules(await getExamModules({ matiere: draft.matiere, classe: draft.classe }));
    } catch (requestError) {
      setModules([]);
      setModulesError(requestError.response?.data?.detail || requestError.response?.data?.matiere || requestError.response?.data?.classe || "Impossible de charger les modules.");
    } finally {
      setModulesLoading(false);
    }
  }, [draft.classe, draft.matiere]);

  useEffect(() => {
    if (!draft.matiere || !draft.classe) {
      setModules([]);
      return;
    }
    loadModules();
  }, [draft.classe, draft.matiere, loadModules]);

  useEffect(() => {
    if (!draft.matiere || !draft.classe) { setExamPositions([]); return undefined; }
    let active = true;
    getExamStructure({ matiere: draft.matiere, classe: draft.classe })
      .then((positions) => { if (active) setExamPositions(positions); })
      .catch(() => { if (active) setExamPositions([]); });
    return () => { active = false; };
  }, [draft.classe, draft.matiere]);

  const moduleKey = draft.moduleIds.join(",");
  const loadLessons = useCallback(async () => {
    const moduleIds = moduleKey.split(",").filter(Boolean).map(Number);
    if (!moduleIds.length) return;
    setLessonsLoading(true);
    setLessonsError(null);
    try {
      setLessons(await getExamLessons(moduleIds));
    } catch (requestError) {
      setLessons([]);
      setLessonsError(requestError.response?.data?.detail || requestError.response?.data?.modules || "Impossible de charger les leçons.");
    } finally {
      setLessonsLoading(false);
    }
  }, [moduleKey]);

  useEffect(() => {
    if (!moduleKey) {
      setLessons([]);
      return;
    }
    loadLessons();
  }, [moduleKey, loadLessons]);

  const onFieldChange = (field, value) => {
    if (field !== "tier") invalidatePreview();
    setDraft((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const onContextChange = (field, value) => {
    invalidatePreview();
    setDraft((current) => ({ ...current, [field]: value, moduleIds: [], lessonIds: [] }));
    setSelectedExercises([]);
    setModules([]);
    setLessons([]);
    setErrors((current) => ({ ...current, [field]: undefined, moduleIds: undefined }));
  };

  const toggleModule = (id) => {
    invalidatePreview();
    setDraft((current) => {
      const selected = current.moduleIds.includes(id)
        ? current.moduleIds.filter((item) => item !== id)
        : [...current.moduleIds, id];
      return { ...current, moduleIds: selected, lessonIds: [] };
    });
    setErrors((current) => ({ ...current, moduleIds: undefined }));
  };

  const toggleLesson = (id) => {
    invalidatePreview();
    setDraft((current) => ({
      ...current,
      lessonIds: current.lessonIds.includes(id)
        ? current.lessonIds.filter((item) => item !== id)
        : [...current.lessonIds, id],
    }));
  };

  const selectionContextKey = `${draft.matiere}|${draft.classe}|${draft.session}|${draft.moduleIds.join(",")}|${draft.lessonIds.join(",")}`;
  useEffect(() => {
    const compatible = selectedExercises.filter((exercise) => isExerciseCompatible(exercise, draft));
    const removed = selectedExercises.length - compatible.length;
    if (removed > 0) {
      setSelectedExercises(compatible);
      setSelectionNotice(`${removed} exercice${removed > 1 ? "s ont" : " a"} été retiré${removed > 1 ? "s" : ""}, car la sélection pédagogique a changé.`);
    }
  // La clé agrège uniquement les critères qui déterminent la compatibilité.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionContextKey]);

  const validateGeneral = () => {
    const nextErrors = {};
    if (!draft.establishmentId) nextErrors.establishmentId = "Sélectionnez un établissement.";
    if (!draft.classe) nextErrors.classe = "Sélectionnez une classe.";
    if (!draft.matiere) nextErrors.matiere = "Sélectionnez une matière.";
    if (!draft.session) nextErrors.session = "Sélectionnez une session.";
    if (!enhancedNativeFlow && !draft.tier) nextErrors.tier = "Sélectionnez un palier.";
    if (!draft.duration.trim()) nextErrors.duration = "La durée est obligatoire.";
    if (draft.coefficient.trim() && !/^\d+(?:[.,]\d+)?$/.test(draft.coefficient.trim())) {
      nextErrors.coefficient = "Saisissez un coefficient numérique positif.";
    }
    setErrors(nextErrors);
    if (enhancedNativeFlow && Object.keys(nextErrors).length) {
      const fieldIds = {
        establishmentId: "exam-establishment",
        classe: "exam-class",
        matiere: "exam-subject",
        session: "exam-session",
        duration: "exam-duration",
        coefficient: "exam-coefficient",
      };
      const first = Object.keys(nextErrors)[0];
      setFlowDialog({
        title: "Champ obligatoire manquant",
        message: nextErrors[first],
        actions: [{ label: "Corriger ce champ", primary: true, onClick: () => closeDialogAndFocus(fieldIds[first]) }],
        onClose: () => closeDialogAndFocus(fieldIds[first]),
      });
    }
    return Object.keys(nextErrors).length === 0;
  };

  const goNext = () => {
    if (step === 1) {
      if (!validateGeneral()) return;
      setStep(2);
    } else if (step === 2) {
      if (!draft.moduleIds.length) {
        setErrors((current) => ({ ...current, moduleIds: "Sélectionnez au moins un module." }));
        if (enhancedNativeFlow) setFlowDialog({ title: "Sélection obligatoire", message: "Sélectionnez au moins un module.", actions: [{ label: "Choisir un module", primary: true, onClick: () => closeDialogAndFocus("exam-module-list") }], onClose: () => closeDialogAndFocus("exam-module-list") });
        return;
      }
      setStep(3);
    } else if (step === 3) {
      if (!selectedExercises.length) {
        setErrors((current) => ({ ...current, exercises: "Sélectionnez au moins un exercice." }));
        if (enhancedNativeFlow) setFlowDialog({ title: "Sélection obligatoire", message: "Sélectionnez au moins un exercice.", actions: [{ label: "Choisir un exercice", primary: true, onClick: () => closeDialogAndFocus("exam-exercise-catalog") }], onClose: () => closeDialogAndFocus("exam-exercise-catalog") });
        return;
      }
      setStep(4);
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const selectExercise = (exercise) => {
    invalidatePreview();
    setSelectedExercises((current) => {
      if (current.some((item) => item.id === exercise.id)) return current;
      const occupied = new Set(current.map((item) => item.positionKey));
      const position = examPositions.find((item) => item.evaluation_type === exercise.evaluation_type && !occupied.has(item.key));
      if (!position) {
        setSelectionNotice("Toutes les positions prévues par le site pour ce type d’exercice sont déjà remplies.");
        return current;
      }
      if (position.key === "phy_comp2" && isTwoSituationExercise(exercise)) {
        setSelectionNotice("La Situation 2 attend un exercice à situation unique (8 pts). Cet exercice de 16 pts doit occuper la Situation 1.");
        return current;
      }
      let next = [...current, { ...exercise, positionKey: position.key, positionLabel: position.label }];
      if (position.key === "phy_comp1" && isTwoSituationExercise(exercise)) {
        next = next.filter((item) => item.positionKey !== "phy_comp2");
        next = next.map((item) => item.id === exercise.id ? { ...item, two_situations: true } : item);
        setSelectionNotice("Cet exercice de compétences contient déjà deux situations (16 pts) : la Situation 2 n’est plus nécessaire.");
      }
      return next;
    });
    setErrors((current) => ({ ...current, exercises: undefined }));
  };
  const removeExercise = (exerciseId) => { invalidatePreview(); setSelectedExercises((current) => current.filter((item) => item.id !== exerciseId)); };
  const moveExercise = (index, direction) => setSelectedExercises((current) => {
    invalidatePreview();
    const target = index + direction;
    if (target < 0 || target >= current.length) return current;
    const reordered = [...current];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    return reordered;
  });
  const dropExercise = (source, target) => setSelectedExercises((current) => {
    invalidatePreview();
    if (source === target || source < 0 || target < 0 || source >= current.length || target >= current.length) return current;
    const reordered = [...current];
    const [moved] = reordered.splice(source, 1);
    reordered.splice(target, 0, moved);
    return reordered;
  });

  const buildGenerationPayload = () => ({
    establishment_id: Number(draft.establishmentId),
    class: draft.classe,
    subject: draft.matiere,
    session: draft.session,
    academic_year: draft.academicYear,
    duration: draft.duration,
    coefficient: draft.coefficient,
    title: draft.title,
    examiner: draft.examiner,
    period: draft.period,
    exam_date: draft.examDate,
    header_style: Number(draft.headerStyle),
    tier: draft.tier || "CLASSIQUE",
    sections: selectedExercises.map((exercise, index) => ({
      position: exercise.positionKey || `section-${index + 1}`,
      evaluation_type: exercise.evaluation_type,
      difficulty: exercise.difficulty,
      module_ids: draft.moduleIds.filter((id) => exercise.modules?.some((item) => item.id === id)),
      lesson_ids: draft.lessonIds.filter((id) => exercise.lessons?.some((item) => item.id === id)),
      exercise_id: exercise.id,
    })),
  });

  const requestPreview = async () => {
    if (!selectedExercises.length || previewLock.current) return;
    previewLock.current = true;
    const payload = buildGenerationPayload();
    setPreviewState((current) => ({ ...current, loading: true, error: null, payload }));
    setStep(4);
    try {
      const blob = await previewExam(payload);
      if (previewUrl.current) URL.revokeObjectURL(previewUrl.current);
      previewUrl.current = URL.createObjectURL(blob);
      setPreviewState({ loading: false, error: null, url: previewUrl.current, payload });
      setGenerationFailure(null);
    } catch (requestError) {
      setPreviewState({ loading: false, error: requestError.response?.data?.detail || "Impossible de produire l’aperçu fidèle de l’épreuve.", url: null, payload });
    } finally {
      previewLock.current = false;
    }
  };

  const runGeneration = async ({ retry = false, payload: previewPayload = null } = {}) => {
    if (generationLock.current) return;
    generationLock.current = true;
    let attempt = generationAttempt.current;
    if (!retry || !attempt?.payload) {
      attempt = { key: attempt?.payload ? createIdempotencyKey() : attempt?.key || createIdempotencyKey(), payload: previewPayload || buildGenerationPayload() };
      generationAttempt.current = attempt;
    }
    setGenerationState((current) => ({ ...current, status: "submitting", error: null, retryAvailable: false }));
    try {
      const response = await createExam(attempt.payload, attempt.key);
      let detail;
      try {
        detail = await getExamStatus(response.data.id);
      } catch {
        detail = { ...attempt.payload, ...response.data };
      }
      setGenerationState({ status: "success", error: null, retryAvailable: false, result: { ...attempt.payload, ...response.data, ...detail, replayed: response.httpStatus === 200 }, refreshing: false, refreshError: null });
    } catch (requestError) {
      const detail = requestError.response?.data?.detail || "";
      const uncertain = !requestError.response
        || requestError.response.status === 503
        || (requestError.response.status === 409 && detail.includes("déjà en cours"));
      const failure = enhancedNativeFlow ? describeGenerationError(requestError) : null;
      setGenerationFailure(failure);
      setGenerationState((current) => ({ ...current, status: failure?.uncertain || uncertain ? "uncertain" : "error", error: failure?.message || generationErrorMessage(requestError), retryAvailable: failure ? failure.action === "retry" : uncertain }));
    } finally {
      generationLock.current = false;
    }
  };

  const generateFromPreview = () => {
    if (!enhancedNativeFlow) {
      runGeneration({ payload: previewState.payload });
      return;
    }
    if (!draft.tier) {
      setFlowDialog({ title: "Palier obligatoire", message: "Choisissez le palier Classique ou Premium avant de confirmer la génération.", actions: [{ label: "Choisir un palier", primary: true, onClick: () => closeDialogAndFocus("exam-preview-tier-CLASSIQUE") }], onClose: () => closeDialogAndFocus("exam-preview-tier-CLASSIQUE") });
      return;
    }
    const payload = { ...previewState.payload, tier: draft.tier };
    const availability = correctionAvailability(selectedExercises);
    if (draft.tier === "PREMIUM" && availability.status !== "complete") {
      const text = availability.status === "partial"
        ? `Les corrections sont partielles (${availability.available} élément${availability.available > 1 ? "s" : ""} sur ${availability.total}).`
        : availability.status === "absent"
          ? "Aucune correction n’est disponible pour les exercices sélectionnés."
          : "La disponibilité des corrections n’a pas pu être confirmée avec cette version des données.";
      const classic = (options.paliers || []).find((item) => item.code === "CLASSIQUE");
      const classicPrice = classic ? `${classic.amount} ${classic.currency || "FCFA"}` : null;
      setFlowDialog({
        title: "Correction Premium indisponible",
        message: classicPrice
          ? `${text} Vous pouvez continuer sans correction au palier Classique (${classicPrice}) ou revenir au choix du palier.`
          : `${text} Le prix du palier sans correction n’est pas disponible. Revenez au choix du palier.`,
        actions: [
          { label: "Changer de palier", onClick: () => closeDialogAndFocus("exam-preview-tier-CLASSIQUE") },
          { label: classicPrice ? `Continuer sans correction — ${classicPrice}` : "Continuer sans correction — tarif indisponible", disabled: !classicPrice, primary: true, onClick: () => { setFlowDialog(null); setDraft((current) => ({ ...current, tier: "CLASSIQUE" })); runGeneration({ payload: { ...payload, tier: "CLASSIQUE" } }); } },
        ],
        onClose: () => closeDialogAndFocus("exam-preview-tier-PREMIUM"),
      });
      return;
    }
    runGeneration({ payload });
  };

  const handleGenerationFailureAction = () => {
    if (!generationFailure) return;
    if (generationFailure.action === "modify") { setGenerationFailure(null); setStep(1); focusField("exam-establishment"); return; }
    if (generationFailure.action === "retry") { runGeneration({ retry: true }); return; }
    window.location.href = "mailto:support@quicklearning-cmr.com?subject=Erreur%20générateur%20d’épreuves";
  };

  const refreshGenerationStatus = async () => {
    const examId = generationState.result?.id;
    if (!examId || generationState.refreshing) return;
    setGenerationState((current) => ({ ...current, refreshing: true, refreshError: null }));
    try {
      const result = await getExamStatus(examId);
      setGenerationState((current) => ({ ...current, result: { ...current.result, ...result }, refreshing: false }));
    } catch (requestError) {
      setGenerationState((current) => ({ ...current, refreshing: false, refreshError: generationErrorMessage(requestError) }));
    }
  };

  const resumeExistingExam = async (event) => {
    event.preventDefault();
    const value = resumeState.examId.trim();
    if (!/^\d+$/.test(value) || Number(value) <= 0) {
      setResumeState((current) => ({ ...current, error: "Saisissez un identifiant numérique valide." }));
      return;
    }
    setResumeState((current) => ({ ...current, loading: true, error: null }));
    try {
      const result = await getExamStatus(Number(value));
      setGenerationState({ status: "success", error: null, retryAvailable: false, result, refreshing: false, refreshError: null });
      setPaymentState({ status: result.paid ? "success" : "idle", error: null });
      setResumeState({ open: false, examId: "", loading: false, error: null });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (requestError) {
      const httpStatus = requestError.response?.status;
      const messages = {
        404: "Épreuve introuvable.",
        401: "Votre session a expiré. Reconnectez-vous.",
        403: "Vous n’êtes pas autorisé à consulter cette épreuve.",
      };
      setResumeState((current) => ({
        ...current,
        loading: false,
        error: messages[httpStatus] || "Impossible de charger cette épreuve.",
      }));
    }
  };

  const reconcilePaidExam = async (examId) => {
    try {
      const result = await getExamStatus(examId);
      setGenerationState((current) => ({
        ...current,
        result: { ...current.result, ...result },
      }));
      if (!result.paid) return false;
      setPaymentState({ status: "success", error: null });
      try {
        const dashboard = await onWalletRefresh?.();
        const balance = dashboard?.wallet?.balance;
        if (Number.isFinite(balance)) onWalletUpdate?.(balance);
      } catch {
        // Le paiement reste confirmé par l'épreuve même si le dashboard est indisponible.
      }
      return true;
    } catch {
      return false;
    }
  };

  const payWithWallet = async () => {
    const examId = generationState.result?.id;
    if (!examId || paymentLock.current || generationState.result?.paid) return;
    paymentLock.current = true;
    setPaymentState({ status: "submitting", error: null });
    try {
      const result = await payExamWithWallet(examId);
      setGenerationState((current) => ({
        ...current,
        result: { ...current.result, ...result },
      }));
      const balance = result.wallet?.balance;
      if (Number.isFinite(balance)) onWalletUpdate?.(balance);
      setPaymentState({ status: "success", error: null });
      onWalletPayment?.();
    } catch (requestError) {
      const httpStatus = requestError.response?.status;
      const uncertain = !requestError.response || httpStatus >= 500;
      if ((httpStatus === 403 || uncertain) && await reconcilePaidExam(examId)) return;

      const backendBalance = requestError.response?.data?.balance;
      if (Number.isFinite(backendBalance)) onWalletUpdate?.(backendBalance);
      const activeCinetPay = httpStatus === 409 && /cinetpay|tentative.+cours/i.test(requestError.response?.data?.detail || "");
      const messages = {
        400: "La demande de paiement est invalide.",
        401: "Votre session a expiré. Reconnectez-vous.",
        403: requestError.response?.data?.detail || "Ce paiement n’est pas autorisé.",
        404: "Cette épreuve est introuvable.",
        409: activeCinetPay
          ? "Une tentative de paiement CinetPay est déjà en cours pour cette épreuve."
          : "Solde insuffisant pour effectuer ce paiement.",
        500: "Le résultat du paiement n’a pas pu être confirmé.",
      };
      setPaymentState({
        status: "error",
        error: uncertain
          ? "Le résultat du paiement n’a pas pu être confirmé. Vérifiez votre connexion puis réessayez."
          : messages[httpStatus] || "Le paiement n’a pas pu être effectué.",
      });
    } finally {
      paymentLock.current = false;
    }
  };

  const applyCinetPayStatus = useCallback(async (data, examId) => {
    const statusValue = normalizeCinetPayStatus(data);
    const messages = {
      FAILED: "Le paiement n’a pas abouti.",
      EXPIRED: "La tentative de paiement a expiré.",
      CANCELLED: "Le paiement a été annulé.",
    };
    if (statusValue === "SUCCESS") {
      clearCinetPayAttempt(examId);
      const exam = data?.exam || await getExamStatus(examId);
      setGenerationState((current) => ({ ...current, result: { ...current.result, ...exam } }));
      setCinetPayState({ status: "SUCCESS", error: null, checking: false, paymentUrl: null });
      return;
    }
    if (CINETPAY_TERMINAL_STATES.has(statusValue)) {
      clearCinetPayAttempt(examId);
      setCinetPayState({ status: statusValue, error: messages[statusValue], checking: false, paymentUrl: null });
      return;
    }
    const attempt = readCinetPayAttempt(examId);
    if (attempt) persistCinetPayAttempt({ ...attempt, status: statusValue });
    setCinetPayState((current) => ({ ...current, status: statusValue, error: null, checking: false }));
  }, []);

  const refreshCinetPayStatus = useCallback(async () => {
    const examId = generationState.result?.id;
    if (!examId || cinetPayState.checking) return;
    setCinetPayState((current) => ({ ...current, checking: true, error: null }));
    try {
      const result = await getCinetPayPaymentStatus(examId);
      await applyCinetPayStatus(result, examId);
    } catch (requestError) {
      const statusCode = requestError.response?.status;
      const detail = requestError.response?.data?.detail;
      setCinetPayState((current) => ({
        ...current,
        status: statusCode === 404 ? "NOT_FOUND" : current.status,
        checking: false,
        error: statusCode === 404
          ? "Aucune tentative CinetPay active n’a été retrouvée."
          : statusCode === 401 || statusCode === 403
            ? detail || "Votre session a expiré. Reconnectez-vous."
            : "Le statut du paiement n’a pas encore pu être confirmé.",
      }));
    }
  }, [applyCinetPayStatus, cinetPayState.checking, generationState.result?.id]);

  useEffect(() => {
    const examId = generationState.result?.id;
    if (!examId || generationState.result?.paid) return undefined;
    const attempt = readCinetPayAttempt(examId);
    if (!attempt) return undefined;
    setCinetPayState((current) => current.status === "idle"
      ? restoredCinetPayState(attempt)
      : current);
    return undefined;
  }, [generationState.result?.id, generationState.result?.paid]);

  useEffect(() => {
    if (!CINETPAY_ACTIVE_STATES.has(cinetPayState.status)) return undefined;
    const timer = window.setInterval(refreshCinetPayStatus, 7000);
    return () => window.clearInterval(timer);
  }, [cinetPayState.status, refreshCinetPayStatus]);

  const startCinetPayPayment = async () => {
    const examId = generationState.result?.id;
    if (!examId || generationState.result?.paid || !claimCinetPayInitialization(cinetPayLock, cinetPayState.status)) return;
    let attempt = readCinetPayAttempt(examId);
    if (!attempt) {
      attempt = { examId: Number(examId), key: createIdempotencyKey(), status: "INITIALIZING", paymentUrl: null, createdAt: new Date().toISOString() };
      persistCinetPayAttempt(attempt);
    }
    setCinetPayState((current) => ({ ...current, status: "INITIALIZING", error: null, checking: false }));
    try {
      const result = await initializeCinetPayPayment(examId, attempt.key);
      const statusValue = normalizeCinetPayStatus(result);
      const paymentUrl = result?.payment_url || result?.payment?.payment_url || null;
      persistCinetPayAttempt({ ...attempt, status: statusValue, paymentUrl });
      setCinetPayState({ status: statusValue, error: null, checking: false, paymentUrl });
      if (paymentUrl) {
        try {
          await openCinetPayExternally(paymentUrl);
        } catch {
          setCinetPayState((current) => ({
            ...current,
            error: "Le paiement a été initialisé, mais l’ouverture de la page CinetPay a échoué. Utilisez uniquement l’actualisation du statut.",
          }));
        }
      }
      if (CINETPAY_TERMINAL_STATES.has(statusValue)) await applyCinetPayStatus(result, examId);
    } catch (requestError) {
      const statusCode = requestError.response?.status;
      const detail = requestError.response?.data?.detail;
      setCinetPayState({
        status: statusCode === 503 ? "UNAVAILABLE" : "UNCERTAIN",
        error: statusCode === 503
          ? "Le paiement CinetPay est temporairement indisponible."
          : statusCode === 401 || statusCode === 403
            ? detail || "Votre session a expiré. Reconnectez-vous."
            : "Le statut du paiement n’a pas encore pu être confirmé.",
        checking: false,
        paymentUrl: attempt.paymentUrl || null,
      });
    } finally {
      releaseCinetPayInitialization(cinetPayLock);
    }
  };

  const startNewExam = () => {
    generationAttempt.current = { key: createIdempotencyKey(), payload: null };
    generationLock.current = false;
    paymentLock.current = false;
    cinetPayLock.current = false;
    setGenerationState({ status: "idle", error: null, retryAvailable: false, result: null, refreshing: false, refreshError: null });
    setPaymentState({ status: "idle", error: null });
    setCinetPayState({ status: "idle", error: null, checking: false, paymentUrl: null });
    setSelectedExercises([]);
    setSelectionNotice("");
    setErrors({});
    setModules([]);
    setLessons([]);
    setDraft({ ...initialDraft, academicYear: options?.defaults?.annee_academique || "", duration: options?.defaults?.duree || "" });
    setStep(1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (loading) return <main className="ql-teacher-state" role="status"><span className="spinner-border text-primary" aria-hidden="true"></span><p>Chargement du générateur…</p></main>;
  if (error || !options) return <main className="ql-teacher-state" role="alert"><i className="bi bi-wifi-off"></i><h1>Options indisponibles</h1><p>{error}</p><button type="button" className="btn btn-primary" onClick={reload}>Réessayer</button><button type="button" className="btn btn-outline-secondary" onClick={onBack}>Retour au dashboard</button></main>;

  if (generationState.result) return <main className="ql-exam-main"><div className="ql-exam-toolbar"><button type="button" className="ql-exam-back" onClick={onBack}><i className="bi bi-arrow-left"></i> Dashboard</button><div><span className="ql-teacher-eyebrow">Assistant</span><h1>Résultat de la génération</h1></div></div><ExamGenerationResult exam={generationState.result} wallet={wallet} paymentState={paymentState} cinetPayState={cinetPayState} refreshing={generationState.refreshing} refreshError={generationState.refreshError} onPay={payWithWallet} onCinetPay={startCinetPayPayment} onRefreshCinetPay={refreshCinetPayStatus} onRefresh={refreshGenerationStatus} onNewExam={startNewExam} /></main>;

  return (
    <main className="ql-exam-main">
      <div className="ql-exam-toolbar">
        <button type="button" className="ql-exam-back" onClick={onBack}><i className="bi bi-arrow-left"></i> Dashboard</button>
        <div><span className="ql-teacher-eyebrow">Assistant</span><h1>Nouvelle épreuve</h1></div>
      </div>

      <section className="ql-exam-resume">
        {!resumeState.open ? (
          <button type="button" onClick={() => setResumeState((current) => ({ ...current, open: true, error: null }))}>
            <i className="bi bi-clock-history" aria-hidden="true"></i>
            Reprendre une épreuve existante
          </button>
        ) : (
          <form onSubmit={resumeExistingExam}>
            <label htmlFor="ql-resume-exam-id">Identifiant de l’épreuve</label>
            <div>
              <input
                id="ql-resume-exam-id"
                type="text"
                inputMode="numeric"
                pattern="[0-9]+"
                value={resumeState.examId}
                onChange={(event) => setResumeState((current) => ({ ...current, examId: event.target.value, error: null }))}
                placeholder="Ex. 252"
                disabled={resumeState.loading}
                autoFocus
              />
              <button type="submit" className="btn btn-outline-primary" disabled={resumeState.loading}>
                {resumeState.loading ? "Chargement…" : "Ouvrir"}
              </button>
              <button type="button" className="btn btn-light" onClick={() => setResumeState({ open: false, examId: "", loading: false, error: null })} disabled={resumeState.loading}>Annuler</button>
            </div>
            {resumeState.error && <span role="alert">{resumeState.error}</span>}
          </form>
        )}
      </section>

      <ol className="ql-exam-steps" aria-label="Progression">
        {["Informations", "Programme", "Exercices", "Synthèse"].map((label, index) => <li key={label} className={step === index + 1 ? "active" : step > index + 1 ? "done" : ""}><span>{index + 1}</span>{label}</li>)}
      </ol>

      {step === 1 && <ExamGeneralOptions draft={draft} errors={errors} establishments={establishments} options={options} onFieldChange={onFieldChange} onContextChange={onContextChange} onManageEstablishments={onManageEstablishments} showTier={!enhancedNativeFlow} />}
      {step === 2 && <ModuleLessonSelector modules={modules} lessons={lessons} selectedModules={draft.moduleIds} selectedLessons={draft.lessonIds} modulesLoading={modulesLoading} lessonsLoading={lessonsLoading} modulesError={modulesError} lessonsError={lessonsError} onToggleModule={toggleModule} onToggleLesson={toggleLesson} onRetryModules={loadModules} onRetryLessons={loadLessons} moduleValidationError={errors.moduleIds} />}
      {step === 3 && <><ExerciseBrowser context={{ matiere: draft.matiere, classe: draft.classe, session: draft.session, moduleIds: draft.moduleIds, lessonIds: draft.lessonIds }} modules={modules.filter((item) => draft.moduleIds.includes(item.id))} lessons={lessons.filter((item) => !draft.lessonIds.length || draft.lessonIds.includes(item.id))} difficulties={options.grades || []} classLabel={getLabel(options.classes || [], draft.classe)} tier={enhancedNativeFlow ? "CLASSIQUE" : draft.tier} selectedExercises={selectedExercises} expectedPositions={examPositions} selectionNotice={selectionNotice} generationState={generationState} onDismissNotice={() => setSelectionNotice("")} onGenerate={requestPreview} onRetryGeneration={requestPreview} onSelect={selectExercise} onRemove={removeExercise} onMove={moveExercise} onDropExercise={dropExercise} />{errors.exercises && <span className="ql-exam-field-error ql-exercise-validation" role="alert">{errors.exercises}</span>}</>}
      {step === 4 && <ExamPreview {...previewState} enhancedFlow={enhancedNativeFlow} tiers={options.paliers || []} selectedTier={draft.tier} onTierChange={(tier) => onFieldChange("tier", tier)} generationFailure={generationFailure} onGenerationAction={handleGenerationFailureAction} generating={generationState.status === "submitting"} onRetry={requestPreview} onModify={() => setStep(3)} onGenerate={generateFromPreview} />}

      {flowDialog && <ExamFlowDialog {...flowDialog} />}

      <div className="ql-exam-actions">
        {step > 1 && <button type="button" className="btn btn-outline-secondary" onClick={() => { setStep((current) => current - 1); window.scrollTo({ top: 0, behavior: "smooth" }); }}>Précédent</button>}
        {step < 3 && <button type="button" className="btn btn-primary" onClick={goNext}>{step >= 2 ? "Continuer" : "Suivant"}<i className="bi bi-arrow-right ms-2"></i></button>}
      </div>
    </main>
  );
}
